import { Field, useField } from 'react-final-form';
import React, { PropsWithChildren, useState, useMemo } from 'react';
import Input from '../Input';
import { Autocomplete } from '@mui/lab';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';

import './index.css';
import { CircularProgress, InputAdornment, useTheme } from '@mui/material';
import Icon from '../Icon';
import ListBox from 'components/ListBox';
import { useTranslation } from 'react-i18next';

const FormAutocomplete = (props: PropsWithChildren<any>) => {
  const {
    name,
    placeholder,
    getOptionLabel = (v: any) => v.title,
    getOptionValue = (v: any) => v,
    unique,
    withSearch,
    onScroll,
    multiple,
    options,
    inputOnChange,
    onChange,
    dataId = 'form-autocomplete',
    'data-testid': dataTestId,
    isOptionEqualToValue = (option: any, value: any) => {
      return typeof value !== 'object'
        ? getOptionValue(option) === value
        : getOptionValue(option) === getOptionValue(value);
    },
    clearInputAfterSelect = true,
    ...restProps
  } = props;

  const { t } = useTranslation();
  const theme = useTheme()

  const infinityScroll = (e: any) => {
    const scrollTop = e.target.scrollTop;
    const scrollHeight = e.target.scrollHeight;
    const clientHeight = e.target.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 1) {
      onScroll && onScroll();
    }
  };

  const handleMultipleChange = (values: any[]) => {
    if (unique) {
      let unique: any[] = [];
      values &&
        values?.map((value) => {
          if (unique.find((item) => item.id === value.id)) {
            return;
          } else {
            unique.push(
              typeof value !== 'object' ? value : getOptionValue(value)
            );
          }
        });
      return unique;
    } else {
      return (
        values &&
        values?.map((value) => {
          return typeof value === 'string' ? value : getOptionValue(value);
        })
      );
    }
  };

  const getLabelByValue = (value: any) => {
    const option = options.find(
      (option: any) => getOptionValue(option) === value
    );
    return option && getOptionLabel(option);
  };

  const [inputValue, setInputValue] = useState('');

  const defaultValue = useMemo(() => [], [])

  return (
    <Field name={name}>
      {({ input, meta }) => {
        return (
          <Autocomplete
            {...restProps}
            data-id={dataId}
            multiple={multiple}
            options={options}
            noOptionsText={t('No options')}
            value={
              !multiple
                ? input.value
                  ? input.value
                  : null
                : input.value
                  ? input.value
                  : defaultValue
            }
            getOptionLabel={(option) => {
              return typeof option !== 'object'
                ? getLabelByValue(option)
                : (getOptionLabel(option) as any);
            }}
            onChange={(e: any, value: any) => {
              onChange && onChange(e, value, input?.value);
              if (clearInputAfterSelect) {
                setInputValue('');
                inputOnChange && inputOnChange('');
              }
              if (multiple) {
                input.onChange(handleMultipleChange(value));
              } else {
                input.onChange(getOptionValue(value));
              }
            }}
            isOptionEqualToValue={isOptionEqualToValue}
            onBlur={input.onBlur}
            ListboxComponent={ListBox}
            ListboxProps={{ onScroll: (e: any) => infinityScroll(e) }}
            error={meta.error}
            loadingText={
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={25} />
              </div>
            }
            popupIcon={restProps.disabled ? null : <KeyboardArrowDownIcon />}
            inputValue={inputValue}
            onInputChange={(e, value) => {
              setInputValue(value);
              inputOnChange && inputOnChange(value);
            }}
            renderInput={(params: any) => (
              <Input
                {...params}
                onFocus={input.onFocus}
                onBlur={restProps.onBlur || input.onBlur}
                placeholder={placeholder}
                error={meta.touched && meta.error}
                helperText={meta.touched && t(meta.error)}
                inputProps={{
                  ...params.inputProps,
                  'data-testid': dataTestId
                }}
                InputProps={{
                  startAdornment: withSearch && (
                    <InputAdornment position="start">
                      <Icon
                        icon="search"
                        style={{ fontSize: 20, color: theme.palette.colors.black }}
                      />
                    </InputAdornment>
                  ),
                  ...params.InputProps,
                }}
              />
            )}
          />
        );
      }}
    </Field>
  );
};

export default FormAutocomplete;
